import { zodResolver } from "@hookform/resolvers/zod";
import { Button, IconButton } from "@hortis/ui/button";
import { DatePicker, createDateLabel } from "@hortis/ui/date-picker";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@hortis/ui/dialog";
import { Divider } from "@hortis/ui/divider";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@hortis/ui/form";
import { Calendar, ChevronDown, Trash3, XClose } from "@hortis/ui/icons";
import { Input } from "@hortis/ui/input";
import { toast } from "@hortis/ui/sonner";
import { Textarea } from "@hortis/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@hortis/ui/tooltip";
import { pipe } from "@mobily/ts-belt";
import { namedOperations } from "generated/graphql";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { queryClient } from "src/app";
import { CountryPicker } from "src/components/pickers/country-picker";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import { updateAccessionDocument } from "src/features/records/accession/graphql";
import { convertDatesToISO } from "src/utils/convert-dates-to-iso";
import { useGqlMutation } from "src/utils/hooks/graphql";
import { twMerge } from "tailwind-merge";
import { z } from "zod";
import { mapCountryOptionToId } from "../../utils/process-form-data";
import { DiscardDialog } from "./discard-dialog";
import { RemoveDialog } from "./remove-dialog";
import type { AccessionDataZod } from "./schema";

// eslint-disable-next-line sonarjs/cognitive-complexity
export const useWildCollectionFieldCount = () => {
  const form = useFormContext<Partial<AccessionDataZod>>();
  const wildCollectionDetails = form.watch("wildCollection");
  const collectionDate = form.watch("wildCollection.collectionDate");
  const collectionCollectors = form.watch(
    "wildCollection.collectionCollectors",
  );
  const collectionCollectorsFieldNumber = form.watch(
    "wildCollection.collectionCollectorsFieldNumber",
  );
  const collectionHabitat = form.watch("wildCollection.collectionHabitat");
  const collectionAssociatedTaxa = form.watch(
    "wildCollection.collectionAssociatedTaxa",
  );
  const collectionRemarks = form.watch("wildCollection.collectionRemarks");
  const countryOption = form.watch("wildCollection.countryOption");
  const collectionStateProvince = form.watch(
    "wildCollection.collectionStateProvince",
  );
  const collectionLocality = form.watch("wildCollection.collectionLocality");
  const collectionLatitude = form.watch("wildCollection.collectionLatitude");
  const collectionLongitude = form.watch("wildCollection.collectionLongitude");
  const collectionCoordinateUncertainty = form.watch(
    "wildCollection.collectionCoordinateUncertainty",
  );
  const collectionElevation = form.watch("wildCollection.collectionElevation");

  if (wildCollectionDetails == null) {
    return 0;
  }

  let fieldCount = 0;
  if (collectionCollectors != null && collectionCollectors !== "") {
    fieldCount++;
  }
  if (collectionDate != null) {
    fieldCount++;
  }
  if (
    collectionCollectorsFieldNumber != null &&
    collectionCollectorsFieldNumber !== ""
  ) {
    fieldCount++;
  }
  if (collectionHabitat != null && collectionHabitat !== "") {
    fieldCount++;
  }
  if (collectionAssociatedTaxa != null && collectionAssociatedTaxa !== "") {
    fieldCount++;
  }
  if (collectionRemarks != null && collectionRemarks !== "") {
    fieldCount++;
  }
  if (countryOption != null) {
    fieldCount++;
  }
  if (collectionStateProvince != null && collectionStateProvince !== "") {
    fieldCount++;
  }
  if (collectionLocality != null && collectionLocality !== "") {
    fieldCount++;
  }
  if (collectionLatitude != null && collectionLatitude !== "") {
    fieldCount++;
  }
  if (collectionLongitude != null && collectionLongitude !== "") {
    fieldCount++;
  }
  if (collectionCoordinateUncertainty != null) {
    fieldCount++;
  }
  if (collectionElevation != null && collectionElevation !== "") {
    fieldCount++;
  }
  return fieldCount;
};

export const countryOptionSchema = z.object({
  codeId: z.string(),
  name: z.string(),
});
export type CountryOption = z.TypeOf<typeof countryOptionSchema>;

export const wildCollectionDataSchema = z.object({
  collectionDate: z.date().nullish(),
  collectionCollectors: z.string().nullish(),
  collectionCollectorsFieldNumber: z.string().nullish(),
  collectionHabitat: z.string().nullish(),
  collectionAssociatedTaxa: z.string().nullish(),
  collectionRemarks: z.string().nullish(),
  countryOption: countryOptionSchema.nullish(),
  collectionStateProvince: z.string().nullish(),
  collectionLocality: z.string().nullish(),
  collectionLatitude: z.string().nullish(),
  collectionLongitude: z.string().nullish(),
  collectionCoordinateUncertainty: z.number().nullish(),
  collectionElevation: z.string().nullish(),
});

export type WildCollectionDataSchema = z.TypeOf<
  typeof wildCollectionDataSchema
>;

export const WildCollectionDialog = ({
  initialData,
  onSubmit,
  children,
  onSuccess,
  accessionId,
}: {
  initialData?: Partial<WildCollectionDataSchema>;
  onSubmit?: (data: WildCollectionDataSchema | null | undefined) => void;
  children?: ReactNode;
  onSuccess?: () => void;
  accessionId?: string;
}) => {
  const [open, setOpen] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const form = useForm<Partial<WildCollectionDataSchema>>({
    defaultValues: initialData,
    resolver: zodResolver(wildCollectionDataSchema),
  });

  const defaultValues = useMemo(() => initialData, [initialData]);

  const updateAccession = useGqlMutation({
    mutation: updateAccessionDocument,
    onError: () => {
      toast.error("Failed to update wild collection details");
    },
    onSuccess: (res) => {
      if (!res.updateAccession.success) {
        toast.error("Failed to update wild collection details");
      }
      toast.success("Wild collection details updated");
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.GetAccession],
      });
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.GetAccessionById],
      });
      setOpen(false);
      onSuccess?.();
      form.reset(defaultValues);
    },
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    form.reset(defaultValues);
  }, [open, form, initialData]);

  const handleSubmit = async (data: WildCollectionDataSchema) => {
    if (onSubmit != null) {
      onSubmit(data as WildCollectionDataSchema);
      setOpen(false);
      return;
    }
    if (accessionId == null) {
      toast.error("Failed to update wild collection details");
      throw new Error("Accession ID is required");
    }
    await updateAccession.mutateAsync({
      id: accessionId,
      updates: pipe(data, convertDatesToISO, mapCountryOptionToId),
    });
  };

  const onOpenChange = (open: boolean) => {
    if (
      !open &&
      !discardDialogOpen &&
      Object.keys(form.formState.dirtyFields).length > 0
    ) {
      setDiscardDialogOpen(true);
    } else {
      setOpen(open);
      setDiscardDialogOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {children}
      <FormProvider {...form}>
        <DialogContent
          asChild
          className="flex max-h-[calc(100%-32px)] max-w-xl flex-col gap-0! bg-grey-25 p-0!"
        >
          <form
            onSubmit={(e) => {
              e.stopPropagation();
              void form.handleSubmit(handleSubmit)(e);
            }}
          >
            <DialogHeader className="px-8 py-4">
              <DialogTitle>Wild collection</DialogTitle>
              <DialogDescription>
                Details about where the material was collected
              </DialogDescription>
              <div className="absolute right-3 top-3 flex items-center">
                {initialData != null && (
                  <Tooltip>
                    <RemoveDialog
                      onDiscard={() => {
                        setOpen(false);
                        if (onSubmit != null) {
                          onSubmit(null);
                          return;
                        }
                        handleSubmit({
                          collectionDate: null,
                          collectionAssociatedTaxa: null,
                          collectionCollectors: null,
                          collectionCollectorsFieldNumber: null,
                          collectionElevation: null,
                          collectionCoordinateUncertainty: null,
                          collectionHabitat: null,
                          collectionLatitude: null,
                          collectionLocality: null,
                          collectionLongitude: null,
                          collectionRemarks: null,
                          collectionStateProvince: null,
                        });
                      }}
                      section="wild collection"
                      loading={form.formState.isSubmitting}
                    >
                      <DialogTrigger
                        asChild
                        onFocusCapture={stopEventPropagation}
                      >
                        <TooltipTrigger asChild>
                          <IconButton
                            icon={Trash3}
                            ariaLabel="Remove wild collection"
                            variant="tertiaryGray"
                            testId="remove-wild-collection-button"
                          />
                        </TooltipTrigger>
                      </DialogTrigger>
                    </RemoveDialog>
                    <TooltipContent side="bottom" sideOffset={0}>
                      Remove wild collection
                    </TooltipContent>
                  </Tooltip>
                )}
                <DialogClose asChild>
                  <IconButton
                    icon={XClose}
                    ariaLabel="Close"
                    variant="tertiaryGray"
                  />
                </DialogClose>
              </div>
            </DialogHeader>
            <div className="mx-2 flex flex-col gap-6 overflow-auto rounded-lg border border-grey-200 bg-white px-6 py-6">
              <FormField
                control={form.control}
                name="collectionDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Collection date</FormLabel>
                    <FormControl>
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                        data-cy="collection-date-picker"
                      >
                        <Button
                          endIcon={Calendar}
                          className="min-h-[44px] justify-between py-2! pl-3.5!"
                        >
                          {field.value == null ? (
                            <span className="text-grey-500">
                              Select collection date...
                            </span>
                          ) : (
                            createDateLabel(field.value)
                          )}
                        </Button>
                      </DatePicker>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="collectionCollectors"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Collector(s)</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? undefined}
                        onChange={field.onChange}
                        data-cy="collection-collectors-input"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="collectionCollectorsFieldNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Collector(s) reference</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? undefined}
                        onChange={field.onChange}
                        data-cy="collection-collectors-reference-input"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="collectionHabitat"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Habitat</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? undefined}
                        onChange={field.onChange}
                        data-cy="collection-habitat-input"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="collectionAssociatedTaxa"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Associated taxa</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? undefined}
                        onChange={field.onChange}
                        data-cy="collection-associated-taxa-input"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="collectionRemarks"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Remarks</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        value={field.value ?? undefined}
                        onChange={field.onChange}
                        data-cy="collection-remarks-input"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Divider />
              <FormField
                control={form.control}
                name="countryOption"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country</FormLabel>
                    <FormControl>
                      <CountryPicker {...field}>
                        <Button
                          className={twMerge("justify-between")}
                          testId="collection-country-picker"
                          endIcon={ChevronDown}
                        >
                          {field.value == null ? (
                            <span className="text-grey-500">
                              Select country...
                            </span>
                          ) : (
                            field.value.name
                          )}
                        </Button>
                      </CountryPicker>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="collectionStateProvince"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Province / region</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? undefined}
                        onChange={field.onChange}
                        data-cy="collection-province-region-input"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="collectionLocality"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Locality</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? undefined}
                        onChange={field.onChange}
                        data-cy="collection-locality-input"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex flex-wrap items-center gap-4">
                <FormField
                  control={form.control}
                  name="collectionLatitude"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>Latitude</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? undefined}
                          onChange={field.onChange}
                          data-cy="collection-latitude-input"
                          className="flex-1"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="collectionLongitude"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>Longitude</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? undefined}
                          onChange={field.onChange}
                          data-cy="collection-longitude-input"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <FormField
                  control={form.control}
                  name="collectionCoordinateUncertainty"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>Uncertainty (m)</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          data-cy="collection-coordinate-uncertainty-input"
                          value={field.value ?? undefined}
                          onChange={(e) => {
                            field.onChange(
                              e.target.value === ""
                                ? undefined
                                : Number(e.target.value),
                            );
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="collectionElevation"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>Elevation (m)</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? undefined}
                          type="number"
                          data-cy="collection-elevation"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <DialogFooter className="px-8 py-4">
              <DiscardDialog
                onDiscard={() => {
                  onOpenChange(false);
                }}
                open={discardDialogOpen}
                onOpenChange={setDiscardDialogOpen}
              >
                <DialogTrigger
                  asChild
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onOpenChange(false);
                  }}
                >
                  <Button
                    variant="tertiaryGray"
                    testId="cancel-wild-collection-button"
                    size="sm"
                  >
                    Cancel
                  </Button>
                </DialogTrigger>
              </DiscardDialog>
              <Button
                variant="primary"
                type="submit"
                testId="save-wild-collection-button"
                size="sm"
              >
                {initialData == null ? "Add" : "Update"} wild collection
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </FormProvider>
    </Dialog>
  );
};
